import React, { useState } from 'react'
import { Icon } from '@iconify/react'
import clsx from 'clsx'

import { Button, Modal, Input } from '../../atoms'
import { useShowMessage } from '../../providers'
import { useCreatePepperDoc, useCreateBrief } from '../../hooks'

import { ReactComponent as EmptyIcon } from '../../assets/brief-empty-state.svg'

import styles from './_.module.css'
import BriefContent from './DocBriefModalContent'

type Props = {
  platform: Omit<Platforms, 'creators'>
  companyId: string
  showDocBriefModal: boolean
  projectCategory?: ProjectCategory
  setShowBriefModal: (val: boolean) => void
  briefs?: BriefList[]
  setSelectedBriefId?: (value: string) => void
  initialBrief?: BriefList
}

export const DocBriefDetailsModal = ({
  platform,
  companyId,
  showDocBriefModal,
  projectCategory,
  setShowBriefModal,
  briefs,
  setSelectedBriefId,
  initialBrief
}: Props): JSX.Element => {
  const [selectedBrief, setSelectedBrief] = useState<BriefList | undefined>(
    initialBrief
  )
  const [searchText, setSearchText] = useState<string>('')
  const createNewDoc = useCreatePepperDoc()
  const createNewBrief = useCreateBrief()
  const showMessage = useShowMessage()

  const filteredBriefList = briefs?.filter(brief =>
    brief.title.toLowerCase().includes(searchText.toLocaleLowerCase())
  )

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const openBrief = (briefId?: string, pepper_doc_id?: string) => {
    if (!briefId) return
    const redirectionUrl =
      platform === 'business'
        ? `/${companyId}/docs/${pepper_doc_id}?context_id=${briefId}`
        : `/docs/${pepper_doc_id}?context_id=${briefId}`
    window.open(redirectionUrl)
  }

  const handleCreateNewBrief = async () => {
    try {
      const newDocResult = await createNewDoc.mutateAsync({
        type: 'briefs',
        body: {
          company_id: companyId,
          ...(projectCategory
            ? { existing_doc: { key: 'category', value: projectCategory } }
            : {})
        }
      })
      const newBriefResult = await createNewBrief.mutateAsync({
        body: { company_id: companyId, pepper_doc_id: newDocResult.id }
      })
      const redirectionUrl =
        platform === 'business'
          ? `/${companyId}/docs/${newDocResult.id}?context_id=${newBriefResult.brief_id}`
          : `/docs/${newDocResult.id}?context_id=${newBriefResult.brief_id}`
      window.open(redirectionUrl, '_blank')
    } catch (error) {
      showMessage({ type: 'error', message: 'Unable to create brief' })
    }
  }

  return (
    <Modal
      visible={showDocBriefModal}
      className={styles.briefModal}
      onCancel={() => setShowBriefModal(false)}
    >
      <div className={styles.briefSearchList}>
        <div className={styles.inputBoxContainer}>
          <Input
            onChange={handleChange}
            prefix={
              <Icon
                icon="majesticons:search-line"
                width={15}
                className={styles.searchIcon}
              />
            }
            placeholder="Find a Brief..."
            className={styles.inputBox}
          />
        </div>
        <div className={styles.briefsList}>
          {filteredBriefList?.map(item => (
            <div
              className={clsx(styles.briefName, {
                [styles.selectedBriefColor]: selectedBrief?.id === item.id
              })}
              key={item.id}
              role="none"
              onClick={() => setSelectedBrief(item)}
            >
              {item?.title}
              <span className={styles.briefCreatorName}>
                {`Created by ${item.creator.first_name} ${item.creator.last_name}`}
              </span>
            </div>
          ))}
        </div>
        <button
          className={styles.createBriefButton}
          onClick={() => {
            handleCreateNewBrief()
            setShowBriefModal(false)
          }}
        >
          <Icon
            icon="ph:plus-circle-bold"
            width={13}
            color="white"
            className={styles.plusIcon}
          />
          Create New Brief
        </button>
      </div>
      <div className={styles.briefDisplay}>
        {selectedBrief && (
          <div className={styles.briefHeader}>
            <div className={styles.briefTitleContainer}>
              <Icon width={12} icon="lucide:file-text" />
              <div className={styles.briefTitle}>
                {`${selectedBrief.title}`}
              </div>
              <span className={styles.line} />
              <div className={styles.briefCreator}>
                Created by{' '}
                {selectedBrief &&
                  `${selectedBrief.creator.first_name} ${selectedBrief.creator.last_name}`}
              </div>
            </div>
            <div className={styles.headerCTAButtons}>
              {setSelectedBriefId && (
                <Button
                  className={styles.useBriefButton}
                  onClick={() => {
                    setSelectedBriefId(selectedBrief?.id)
                    setShowBriefModal(false)
                  }}
                >
                  Use this Brief
                </Button>
              )}
              <Button
                className={styles.openBriefInDocButton}
                onClick={() =>
                  openBrief(selectedBrief?.id, selectedBrief?.pepper_doc_id)
                }
              >
                Open in docs
                <Icon icon="tabler:external-link" className={styles.extLink} />
              </Button>
              <div
                className={styles.modalCloseButton}
                role="none"
                onClick={() => setShowBriefModal(false)}
              >
                <Icon icon="lucide:x" className={styles.modalCloseIcon} />
              </div>
            </div>
          </div>
        )}
        {!selectedBrief && (
          <div className={styles.briefEmptyHeader}>
            <div
              className={styles.modalCloseButton}
              role="none"
              onClick={() => setShowBriefModal(false)}
            >
              <Icon icon="lucide:x" className={styles.modalCloseIcon} />
            </div>
          </div>
        )}
        <div className={styles.briefContent}>
          {!selectedBrief && (
            <div className={styles.blankCanvas}>
              <EmptyIcon className={styles.blankCanvasIcon} />
              <div className={styles.blankTextHeader}>
                Beat the blank canvas
              </div>
              <div className={styles.blankText}>
                <div>Create new Templates or use existing ones</div>
                <div>to help creators jump-start their work</div>
              </div>
            </div>
          )}
          {selectedBrief && (
            <BriefContent pepperDocId={selectedBrief.pepper_doc_id} />
          )}
        </div>
      </div>
    </Modal>
  )
}
